import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";

// ─────────────────────────────────────────────────────────────
//  Contact form posts directly to Formspree (same endpoint as
//  the legacy infinagonrecordings.com site).
//  To change recipient: log into Formspree and update form
//  destination — or replace the URL below.
// ─────────────────────────────────────────────────────────────
const FORMSPREE_URL = "https://formspree.io/f/xkgvdlov";

const SERVICES = ["Home Recording Consultation", "Mixing", "Mastering", "Custom"];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    service: "Home Recording Consultation",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(FORMSPREE_URL, form, {
        headers: { Accept: "application/json" },
      });
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", service: "Home Recording Consultation", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white border-t-2 border-black"
    >
      <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="mb-12">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-black/60 mb-4">
            [ 005 / Let's Talk ]
          </p>
          <h2 className="font-display uppercase leading-[0.82] tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
            Contact
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-black">
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 p-8 md:p-12 border-b-2 lg:border-b-0 lg:border-r-2 border-black"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field
                label="01 · Your Name"
                htmlFor="c-name"
              >
                <input
                  id="c-name"
                  data-testid="contact-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="w-full bg-transparent border-b-2 border-black/25 focus:border-black outline-none py-3 text-lg"
                  placeholder="Rasutekh Infinagon"
                  required
                />
              </Field>
              <Field label="02 · Email" htmlFor="c-email">
                <input
                  id="c-email"
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full bg-transparent border-b-2 border-black/25 focus:border-black outline-none py-3 text-lg"
                  placeholder="you@domain.com"
                  required
                />
              </Field>
            </div>

            <div className="mt-10">
              <Field label="03 · Service" htmlFor="c-service">
                <select
                  id="c-service"
                  data-testid="contact-service"
                  value={form.service}
                  onChange={(e) => update("service", e.target.value)}
                  className="w-full bg-transparent border-b-2 border-black/25 focus:border-black outline-none py-3 text-lg appearance-none cursor-pointer"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="mt-10">
              <Field label="04 · Project Details" htmlFor="c-msg">
                <textarea
                  id="c-msg"
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="w-full bg-transparent border-b-2 border-black/25 focus:border-black outline-none py-3 text-lg resize-none"
                  placeholder="Tell us about your project"
                  required
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-12 group font-mono text-[11px] tracking-[0.25em] uppercase bg-black text-white px-8 py-5 border-2 border-black hover:bg-white hover:text-black transition-colors flex items-center gap-4 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Message"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          {/* Studio info */}
          <aside className="lg:col-span-5 bg-black text-white p-8 md:p-12 flex flex-col justify-between">
            <div>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/60 mb-8">
                Studio Information
              </p>
              <ul className="space-y-7">
                <InfoRow
                  icon={<Mail size={18} />}
                  label="Email"
                  value="rasheed@infinagonrecordings.com"
                  href="mailto:rasheed@infinagonrecordings.com"
                />
                <InfoRow
                  icon={<Phone size={18} />}
                  label="Phone"
                  value="(919) 809-6053"
                  href="tel:+19198096053"
                />
                <InfoRow
                  icon={<MapPin size={18} />}
                  label="Address"
                  value={`4030 Wake Forest Road\nSte 349 · Raleigh, NC 27609`}
                />
                <InfoRow
                  icon={<Clock size={18} />}
                  label="Hours"
                  value="Mon–Sat · 08:00 – 17:00"
                />
              </ul>
            </div>
            <div className="mt-10 border-t border-white/20 pt-6 font-mono text-[11px] tracking-[0.25em] uppercase text-white/55">
              Prefer to book directly?{" "}
              <a
                href="https://engineears.com/infinagon"
                target="_blank"
                rel="noreferrer"
                className="underline text-white"
              >
                EngineEars ↗
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="block font-mono text-[11px] tracking-[0.25em] uppercase text-black/60 mb-2">
      {label}
    </span>
    {children}
  </label>
);

const InfoRow = ({ icon, label, value, href }) => (
  <li className="flex gap-4">
    <span className="pt-1 text-white/75">{icon}</span>
    <span>
      <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-white/55 mb-1">
        {label}
      </span>
      {href ? (
        <a href={href} className="whitespace-pre-line hover:text-white/70">
          {value}
        </a>
      ) : (
        <span className="whitespace-pre-line">{value}</span>
      )}
    </span>
  </li>
);

export default Contact;
